import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { Box, FormControl, MenuItem, Select } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useNoteInsightsDateRange } from 'modules/Insights/Notes/hooks/useNoteInsightsDateRange';
import { Period } from 'graphql/types';
import { useComboboxRole } from 'modules/common/hooks/useComboboxRole';
import { useMessage } from 'modules/intl';
import { useMenuProps } from 'modules/common/hooks/useMenuProps';
import { NoteInsightsViewOptions } from '../../Notes/NoteInsightsContextProvider';

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: theme.spacing(18.25)
  },
  hidden: {
    display: 'none'
  },
  show: {
    display: 'block'
  }
}));

const useSelectStyles = makeStyles((theme) => ({
  outlined: {
    padding: theme.spacing(1)
  }
}));

type InsightsViewOptionsSelectorProp = {
  setDisablePrevious: Dispatch<SetStateAction<boolean>>;
  setDisableNext: Dispatch<SetStateAction<boolean>>;
  disablePrevious: boolean;
  viewOptions: {
    value: NoteInsightsViewOptions;
    text: string;
  }[];
  currentPeriod: Period | null;
  setCurrentPeriod: (period: Period) => void;
  selectedViewOption: NoteInsightsViewOptions;
  setSelectedViewOption: (selectedViewOption: NoteInsightsViewOptions) => void;
};
export const InsightsViewOptionsSelector: React.FC<InsightsViewOptionsSelectorProp> = ({
  setDisablePrevious,
  setDisableNext,
  disablePrevious,
  viewOptions,
  currentPeriod,
  setCurrentPeriod,
  selectedViewOption,
  setSelectedViewOption
}: InsightsViewOptionsSelectorProp) => {
  const classes = useStyles();
  const selectStyles = useSelectStyles();

  const { updateCurrentPeriod } = useNoteInsightsDateRange({
    currentPeriod,
    selectedViewOption,
    setCurrentPeriod,
    setSelectedViewOption,
    setDisablePrevious,
    disablePrevious,
    setDisableNext
  });

  const handleChange = useCallback(
    (event) => {
      updateCurrentPeriod(event.target.value as NoteInsightsViewOptions);
    },
    [updateCurrentPeriod]
  );
  const showCustomOption = useMemo(
    () =>
      selectedViewOption === NoteInsightsViewOptions.CUSTOM
        ? classes.show
        : classes.hidden,
    [classes.hidden, classes.show, selectedViewOption]
  );
  const comboboxLabel = useMessage('dropDownLabel.TimeRange') || '';

  const comboboxRole = useComboboxRole({ label: comboboxLabel });
  const menuProps = useMenuProps({ label: comboboxLabel });
  return (
    <Box
      component="span"
      className={classes.root}
      data-testid="insights-view-option-selector"
    >
      <FormControl variant="outlined" className={classes.root}>
        <Select
          classes={selectStyles}
          data-testid="insights-view-option-simple-select"
          value={selectedViewOption}
          onChange={handleChange}
          SelectDisplayProps={comboboxRole}
          MenuProps={menuProps}
        >
          <MenuItem
            value={NoteInsightsViewOptions.CUSTOM}
            className={showCustomOption}
          >
            <FormattedMessage id="insights.viewOptions.custom" />
          </MenuItem>
          {viewOptions.map(({ value, text }) => {
            return (
              <MenuItem
                key={value}
                value={value}
                data-testid={`smart-diary-option_${value}`}
              >
                {text}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};
